/**
 * CDP SDK — URL scrubbing and sensitive pages (NEWS-LOGIN-IDENTITY-2026-10-26 §5.3, Р-ID8).
 *
 * Measured 2026-10-26: the SDK sent `location.href` and `document.referrer` as they were, so
 * password-reset tokens reached ClickHouse on both news sites (24chasa even counted
 * `/password/reset/<token>` as an article — ~930 rows since 22.06) and e-mail addresses sat in the
 * page URLs of the shops (optibox 122, Paga Shop 43 in 30 days).
 *
 * Two rules, applied to every event in processAndSendEvent:
 *  - a password page (reset, forgot, change) sends NOTHING — not a page view, not an article event;
 *  - every URL that leaves the page loses its secrets: the value of a secret query parameter, a
 *    token-shaped path segment, an e-mail address anywhere, userinfo before the host.
 *
 * KEEP IN SYNC with packages/server/src/utils/url-scrub.ts — the server applies the same rules at
 * ingest for bundles still cached in browsers (Cloudflare serves the SDK with max-age 4 h). The
 * server test runs one set of vectors against both copies.
 */

/** Query/fragment parameters whose VALUE never leaves the page (key kept, value replaced). */
const SECRET_PARAMS = new Set([
  'token', 'access_token', 'id_token', 'refresh_token', 'auth', 'authorization', 'code',
  'key', 'apikey', 'api_key', 'signature', 'sig', 'hash', 'otp', 'password', 'pass', 'pwd',
  'passwd', 'secret', 'session', 'sessionid', 'jwt', 'reset', 'reset_token', 'login',
  'email', 'e-mail', 'mail', 'phone', 'tel',
]);

/** A path segment after one of these words is a token when it is long and opaque. */
const TOKEN_AFTER = new Set([
  'reset', 'verify', 'verification', 'activate', 'activation', 'confirm', 'confirmation',
  'invite', 'invitation', 'unsubscribe', 'magic', 'token',
]);

/** Path segments that mark a password page: nothing is sent from it. Exact segment match, so a
 *  long article slug that merely contains the word ("kak-da-smenim-parolata") is not affected. */
const PASSWORD_SEGMENTS = new Set([
  'password', 'passwords', 'parola', 'lost-password', 'reset-password', 'password-reset',
  'forgot-password', 'forgotten-password', 'forgot', 'change-password', 'new-password',
]);

const REDACTED = 'redacted';
const REDACTED_EMAIL = 'redacted-email';
const EMAIL_RE = /[A-Za-z0-9._%+-]+(?:@|%40)[A-Za-z0-9.-]+\.[A-Za-z]{2,}/gi;
/** ≥ 40 letters/digits/underscore with at least one of each and NO hyphen — Laravel's 64-hex reset
 *  token, a 40-hex signed-URL hash. dir.bg's article slugs are hyphenated and 24chasa's article ids
 *  are digits only, so neither matches; 40 (not 24) so a CMS with 24-hex ObjectIds or 32-hex ids in
 *  its article URLs keeps them — collapsing those would merge every article into one. */
const TOKEN_SEGMENT = /^(?=[A-Za-z0-9_]*\d)(?=[A-Za-z0-9_]*[A-Za-z])[A-Za-z0-9_]{40,}$/;
/** After a TOKEN_AFTER word: any long opaque value (base64url, dotted JWT). */
const TOKEN_AFTER_SEGMENT = /^[A-Za-z0-9_.=~-]{16,}$/;
const URL_PARTS = /^([a-z][a-z0-9+.-]*:\/\/[^/?#]*)?([^?#]*)(\?[^#]*)?(#.*)?$/i;

function safeDecode(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

/** Mask token-shaped segments and e-mail addresses in a path. Unchanged input → same string. */
export function scrubPath(path: string): string {
  if (!path) return path;
  const segs = path.split('/');
  let changed = false;
  for (let i = 0; i < segs.length; i++) {
    const seg = segs[i]!;
    if (!seg) continue;
    const prev = i > 0 ? safeDecode(segs[i - 1]!).toLowerCase() : '';
    if (
      TOKEN_SEGMENT.test(seg) ||
      (TOKEN_AFTER.has(prev) && TOKEN_AFTER_SEGMENT.test(seg))
    ) {
      segs[i] = ':token';
      changed = true;
    }
  }
  let out = changed ? segs.join('/') : path;
  if (EMAIL_RE.test(out)) out = out.replace(EMAIL_RE, REDACTED_EMAIL);
  EMAIL_RE.lastIndex = 0;
  return out;
}

/** Mask secret parameter values and e-mails in `k=v&…` (no leading `?`/`#`). */
function scrubParams(params: string): string {
  if (!params) return params;
  let changed = false;
  const parts = params.split('&').map((part) => {
    const eq = part.indexOf('=');
    if (eq <= 0) return part;
    const key = safeDecode(part.slice(0, eq).replace(/\+/g, ' ')).trim().toLowerCase();
    if (SECRET_PARAMS.has(key) && part.slice(eq + 1) !== REDACTED) {
      changed = true;
      return `${part.slice(0, eq)}=${REDACTED}`;
    }
    return part;
  });
  let out = changed ? parts.join('&') : params;
  if (EMAIL_RE.test(out)) out = out.replace(EMAIL_RE, REDACTED_EMAIL);
  EMAIL_RE.lastIndex = 0;
  return out;
}

/** Scrub a full URL or a bare path (+ query + fragment). Non-strings and '' pass through. */
export function scrubUrl(url: string): string {
  if (typeof url !== 'string' || !url) return url;
  const m = URL_PARTS.exec(url);
  if (!m) return url.replace(EMAIL_RE, REDACTED_EMAIL);
  let origin = m[1] || '';
  // `https://user:pass@host/…` — the userinfo never leaves.
  const at = origin.lastIndexOf('@');
  if (at !== -1) origin = origin.replace(/^([a-z][a-z0-9+.-]*:\/\/)[^/?#]*@/i, '$1');
  const path = scrubPath(m[2] || '');
  const search = m[3] ? '?' + scrubParams(m[3].slice(1)) : '';
  let hash = m[4] || '';
  if (hash) {
    const body = hash.slice(1);
    const q = body.indexOf('?');
    // `#k=v&…` (implicit-flow tokens) or `#/route?k=v`
    if (q !== -1) hash = '#' + body.slice(0, q) + '?' + scrubParams(body.slice(q + 1));
    else if (body.includes('=')) hash = '#' + scrubParams(body);
    else hash = '#' + scrubPath(body);
  }
  const out = origin + path + search + hash;
  return out === url ? url : out;
}

/** Glob (`*` = any run) → anchored RegExp; a pattern with a dot before the first `/` names a host. */
function globToRegExp(glob: string): RegExp {
  const esc = glob.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*');
  return new RegExp('^' + esc + '$', 'i');
}

/**
 * Whether nothing may be sent from this page: a password page by the built-in segment list, or a
 * site's own `privacy.excludePaths` (globs on the path, or on host + path when the pattern starts
 * with a host, e.g. `profile.dir.bg/reset/*`).
 */
export function isSensitivePath(pathname: string, host?: string, extra?: readonly string[]): boolean {
  const path = pathname || '/';
  const segs = path.split('/').filter(Boolean).map((s) => safeDecode(s).toLowerCase());
  if (segs.some((s) => PASSWORD_SEGMENTS.has(s))) return true;
  if (segs[0] === 'reset') return true;
  if (segs[0] === 'email' && segs[1] === 'verify') return true;
  if (segs[0] === 'verify-email') return true;
  if (extra && extra.length) {
    const withHost = (host || '') + path;
    for (const p of extra) {
      if (typeof p !== 'string' || !p) continue;
      const slash = p.indexOf('/');
      const hasHost = slash > 0 && p.slice(0, slash).includes('.');
      try {
        if (globToRegExp(p).test(hasHost ? withHost : path)) return true;
      } catch {
        /* a broken pattern never blocks tracking */
      }
    }
  }
  return false;
}

/** Keys holding a URL or a path, in `properties`, `context.page` and `context.session`. */
const URL_KEYS = new Set([
  'url', 'href', 'referrer', 'landingPage', 'landing_page', 'pageUrl', 'page_url',
  'canonicalUrl', 'canonical_url', 'referrerUrl',
]);
const PATH_KEYS = new Set(['path', 'pagePath', 'page_path']);
const SEARCH_KEYS = new Set(['search']);

function scrubFields(obj: unknown): unknown {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
  let out: Record<string, unknown> | null = null;
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    if (typeof v !== 'string' || !v) continue;
    let next = v;
    if (URL_KEYS.has(k)) next = scrubUrl(v);
    else if (PATH_KEYS.has(k)) next = scrubUrl(v);
    else if (SEARCH_KEYS.has(k)) next = v.startsWith('?') ? '?' + scrubParams(v.slice(1)) : scrubParams(v);
    if (next !== v) {
      out = out || { ...(obj as Record<string, unknown>) };
      out[k] = next;
    }
  }
  return out || obj;
}

/**
 * The event with every URL scrubbed — `properties`, `context.page`, `context.session`. Returns the
 * SAME object when nothing changed (the common case), otherwise a copy; never mutates the input.
 */
export function scrubEventPayload<T>(payload: T): T {
  const p = payload as unknown as Record<string, unknown>;
  if (!p || typeof p !== 'object') return payload;
  const props = scrubFields(p.properties);
  const ctx = p.context as Record<string, unknown> | undefined;
  let nextCtx: Record<string, unknown> | undefined = ctx;
  if (ctx && typeof ctx === 'object') {
    const page = scrubFields(ctx.page);
    const session = scrubFields(ctx.session);
    if (page !== ctx.page || session !== ctx.session) nextCtx = { ...ctx, page, session };
  }
  if (props === p.properties && nextCtx === ctx) return payload;
  return { ...p, properties: props, context: nextCtx } as unknown as T;
}
